import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useNetworkStore } from '../stores/networkStore';

interface LastUpdatedBannerProps {
  dataUpdatedAt: number;
  isStale: boolean;
}

function formatElapsed(dataUpdatedAt: number): string | null {
  if (!dataUpdatedAt) return null;
  const diff = Date.now() - dataUpdatedAt;
  if (diff < 60_000) return 'Updated just now';
  if (diff < 3_600_000) return `Updated ${Math.floor(diff / 60_000)} min ago`;
  return `Updated ${Math.floor(diff / 3_600_000)}h ago`;
}

/**
 * Shows "Updated X ago" when we're stuck displaying old cached data because
 * the network is down. Only surfaces when staleness means "we couldn't
 * refresh" — not merely "React Query's staleTime has elapsed". On a healthy
 * network, staleness is transient (refetchOnMount clears it), so this banner
 * stays hidden.
 */
export default function LastUpdatedBanner({ dataUpdatedAt, isStale }: LastUpdatedBannerProps) {
  const connectivityState = useNetworkStore((s) => s.connectivityState);
  const shouldShow = isStale && connectivityState !== 'online';
  const [text, setText] = useState(() => formatElapsed(dataUpdatedAt));

  useEffect(() => {
    setText(formatElapsed(dataUpdatedAt));
    if (!shouldShow || !dataUpdatedAt) return;
    const id = setInterval(() => setText(formatElapsed(dataUpdatedAt)), 30_000);
    return () => clearInterval(id);
  }, [dataUpdatedAt, shouldShow]);

  if (!shouldShow || !dataUpdatedAt || !text) return null;

  return (
    <View style={styles.banner} testID="last-updated-banner" accessibilityLabel={text}>
      <Text style={styles.text}>{text}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#fef3c7',
    paddingHorizontal: 16,
    paddingVertical: 6,
    alignItems: 'center',
  },
  text: {
    fontSize: 12,
    color: '#b45309',
    fontWeight: '500',
  },
});
